import { useState } from 'react';
import { useAddControlsFromLibraryMutation } from 'src/graphql/_generated';
import { useAuth } from 'src/state/auth';
import { useControlCostVariableReview } from './useControlCostVariableReview';

interface UseAddControlsWithVariableReviewConfig {
  /** Per-site completion — the call sites genuinely differ (refetch keys, toasts, audit). */
  afterSuccess: (count: number) => void | Promise<void>;
  onError?: (err: unknown) => void;
}

interface RequestAddOpts {
  addRecommendedModifiers?: boolean;
}

/**
 * Wraps `addControlsFromLibrary` with the Missing-Variables review (nudge): the BE
 * `controlCostMissingVariables` query reports which variables the selected templates'
 * cost formulas reference that are unpopulated in the workspace — when any, the modal
 * opens first (Save & Proceed populates them, Skip adds anyway). The BE copies the
 * template formulas onto the created controls (copy-once, CRPD-7222). The consumer
 * renders `reviewModal` in its tree. Detection/review orchestration lives in
 * `useControlCostVariableReview`; this wrapper only owns the add mutation.
 */
export const useAddControlsWithVariableReview = ({ afterSuccess, onError }: UseAddControlsWithVariableReviewConfig) => {
  const { workspaceId } = useAuth();
  const [addControlsFromLibrary] = useAddControlsFromLibraryMutation({ onError });
  const [isAdding, setIsAdding] = useState(false);
  const { requestWithReview, reviewModal, isChecking } = useControlCostVariableReview({ onError });

  const doAdd = async (ids: string[], opts?: RequestAddOpts) => {
    setIsAdding(true);
    try {
      await addControlsFromLibrary({
        variables: {
          workspaceId,
          controlIds: ids.map((id) => ({ id })),
          addRecommendedModifiers: opts?.addRecommendedModifiers,
        },
      });
      await afterSuccess(ids.length);
    } finally {
      setIsAdding(false);
    }
  };

  const requestAdd = (ids: string[], opts?: RequestAddOpts) =>
    requestWithReview({ frameworkControlIds: ids, destination: 'rmp', plural: ids.length > 1 }, () =>
      doAdd(ids, opts),
    );

  return { requestAdd, reviewModal, isAdding: isAdding || isChecking };
};
